<script setup lang="ts">
const props = withDefaults(defineProps<{
  color?: string
  size?: 'x-small' | 'small' | 'default' | 'large'
  variant?: 'flat' | 'outlined' | 'text' | 'tonal' | 'elevated'
  closable?: boolean
  label?: boolean
  prependIcon?: string
  appendIcon?: string
  to?: string | object
  href?: string
  disabled?: boolean
  density?: 'default' | 'comfortable' | 'compact'
}>(), {
  size: 'default',
  variant: 'tonal',
  density: 'default',
})

const emit = defineEmits<{
  'click:close': []
  click: [e: MouseEvent]
}>()

const NuxtLink = defineNuxtLink({})
</script>

<template>
  <component
    :is="to ? NuxtLink : href ? 'a' : 'span'"
    v-bind="to ? { to } : href ? { href } : {}"
    class="m-chip"
    :class="[
      `m-chip--${variant}`,
      `m-chip--${size}`,
      {
        'm-chip--label': label,
        'm-chip--disabled': disabled,
        'm-chip--clickable': to || href || $attrs.onClick,
        [`m-chip--${color}`]: color,
      }
    ]"
    @click="emit('click', $event as MouseEvent)"
  >
    <MIcon v-if="prependIcon" :icon="prependIcon" :size="size === 'x-small' ? 'x-small' : 'small'" class="m-chip__prepend" />
    <span class="m-chip__content"><slot /></span>
    <MIcon v-if="appendIcon" :icon="appendIcon" :size="size === 'x-small' ? 'x-small' : 'small'" class="m-chip__append" />
    <button v-if="closable" class="m-chip__close" @click.stop="emit('click:close')" aria-label="Remove">
      <MIcon icon="close" size="x-small" />
    </button>
  </component>
</template>

<style scoped>
.m-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--m-space-1);
  border-radius: var(--m-radius);
  font-family: var(--m-font-body);
  font-weight: 500;
  white-space: nowrap;
  text-decoration: none;
  transition: all var(--m-transition);
  vertical-align: middle;
}

.m-chip--label { border-radius: var(--m-radius-sm); }

/* Sizes */
.m-chip--x-small { padding: 0 6px; font-size: 10px; height: 20px; }
.m-chip--small { padding: 0 8px; font-size: var(--m-font-size-xs); height: 24px; }
.m-chip--default { padding: 0 12px; font-size: var(--m-font-size-sm); height: 30px; }
.m-chip--large { padding: 0 16px; font-size: var(--m-font-size-base); height: 36px; }

/* Variants — default (no color) */
.m-chip--tonal { background: var(--m-surface-variant); color: var(--m-on-surface-variant); }
.m-chip--flat { background: var(--m-surface-variant); color: var(--m-on-surface); }
.m-chip--outlined { background: transparent; border: 1px solid var(--m-border); color: var(--m-on-surface); }
.m-chip--text { background: transparent; color: var(--m-on-surface); }
.m-chip--elevated { background: var(--m-surface); color: var(--m-on-surface); box-shadow: var(--m-shadow-sm); }

/* Color chips */
.m-chip--primary.m-chip--tonal { background: color-mix(in srgb, var(--m-primary) 15%, transparent); color: var(--m-primary); }
.m-chip--primary.m-chip--flat { background: var(--m-primary); color: var(--m-on-primary); }
.m-chip--primary.m-chip--outlined { border-color: var(--m-primary); color: var(--m-primary); }

.m-chip--secondary.m-chip--tonal { background: color-mix(in srgb, var(--m-secondary) 15%, transparent); color: var(--m-secondary); }
.m-chip--secondary.m-chip--flat { background: var(--m-secondary); color: var(--m-on-secondary); }

.m-chip--success.m-chip--tonal { background: color-mix(in srgb, var(--m-success) 15%, transparent); color: var(--m-success); }
.m-chip--success.m-chip--flat { background: var(--m-success); color: #fff; }
.m-chip--success.m-chip--outlined { border-color: var(--m-success); color: var(--m-success); }

.m-chip--error.m-chip--tonal { background: color-mix(in srgb, var(--m-error) 15%, transparent); color: var(--m-error); }
.m-chip--error.m-chip--flat { background: var(--m-error); color: #fff; }
.m-chip--error.m-chip--outlined { border-color: var(--m-error); color: var(--m-error); }

.m-chip--warning.m-chip--tonal { background: color-mix(in srgb, var(--m-warning) 15%, transparent); color: var(--m-warning); }
.m-chip--warning.m-chip--flat { background: var(--m-warning); color: #333; }

.m-chip--info.m-chip--tonal { background: color-mix(in srgb, var(--m-info) 15%, transparent); color: var(--m-info); }
.m-chip--info.m-chip--flat { background: var(--m-info); color: #fff; }

/* Clickable */
.m-chip--clickable { cursor: pointer; }
.m-chip--clickable:hover { filter: brightness(0.92); }

/* Disabled */
.m-chip--disabled { opacity: 0.5; pointer-events: none; }

/* Close button */
.m-chip__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  margin-left: var(--m-space-1);
  color: inherit;
  opacity: 0.7;
}
.m-chip__close:hover { opacity: 1; }
</style>
